import { log } from 'evlog/client';
import { Trash2 } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import type { GetRefundEligibility } from '@virtual-care/client';
import { request } from '@virtual-care/client';
import { Button } from '@virtual-care/shadcn';
import { CancelDialog, client, useAction } from '@virtual-care/ui';

export function CancelButton({
  reservationId,
  reservationNumber,
  isB2B,
}: {
  reservationId: string;
  reservationNumber: number;
  isB2B: boolean;
}) {
  const { t } = useTranslation();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [refundEligibility, setRefundEligibility] =
    useState<GetRefundEligibility | null>(null);
  const [isCheckingRefund, setIsCheckingRefund] = useState(false);

  const cancelReservation = useAction('PATCH /reservations/{id}/cancel', {
    invalidate: ['GET /patients/me/reservations', 'GET /reservations/{id}'],
  });

  const handleCancelClick = async () => {
    log.info({ action: 'cancel_reservation_check', reservationId });
    if (isB2B) {
      setIsDialogOpen(true);
      return;
    }

    setIsCheckingRefund(true);

    try {
      const result = await request(
        client,
        'GET /reservations/{id}/refund-eligibility',
        { id: reservationId },
      );

      setRefundEligibility(result.data);

      if (!result.data.canCancel) {
        const reason = result.data.reason ?? '';
        const fallback = t('appointments.actions.cancel.fallback');
        toast.error(
          reason
            ? t(`appointments.actions.cancel.reasons.${reason}`)
            : fallback,
        );
        return;
      }

      setIsDialogOpen(true);
    } catch {
      toast.error(t('appointments.actions.cancel.checkError'));
    } finally {
      setIsCheckingRefund(false);
    }
  };

  const handleCancel = async () => {
    log.info({ action: 'cancel_reservation_confirm', reservationId });
    await cancelReservation.mutateAsync(
      { id: reservationId },
      {
        onSuccess: () => {
          const refundMsg =
            refundEligibility?.canCancel && refundEligibility.refundAmount
              ? t('appointments.actions.cancel.refundSuffix', {
                  amount: (refundEligibility.refundAmount / 100).toFixed(2),
                  currency:
                    refundEligibility.currency === 'SAR'
                      ? t('appointments.detail.currencySar')
                      : (refundEligibility.currency ?? ''),
                })
              : '';
          toast.success(
            `${t('appointments.actions.cancel.success')}${refundMsg}`,
          );
          setIsDialogOpen(false);
          setRefundEligibility(null);
        },
      },
    );
  };

  return (
    <>
      <Button
        variant="outline"
        className="border-destructive text-destructive hover:text-destructive cursor-pointer gap-2 text-sm font-bold"
        disabled={cancelReservation.isPending || isCheckingRefund}
        onClick={handleCancelClick}
      >
        {isCheckingRefund
          ? t('appointments.actions.cancel.checking')
          : t('appointments.actions.cancel.trigger')}{' '}
        <Trash2 className="h-5 w-5" />
      </Button>

      <CancelDialog
        open={isDialogOpen}
        onOpenChange={(open) => {
          setIsDialogOpen(open);
          if (!open) {
            setRefundEligibility(null);
          }
        }}
        reservationNumber={reservationNumber}
        onConfirm={handleCancel}
        isPending={cancelReservation.isPending}
        refundAmount={refundEligibility?.refundAmount}
        currency={refundEligibility?.currency}
        deadlineAt={refundEligibility?.deadlineAt}
      />
    </>
  );
}
